import { useState, useEffect, useRef } from 'react'
import { Search, Globe, Loader2, MapPin, ChevronDown, Navigation } from 'lucide-react'
import { useCourseSearch, useOnlineCourseSearch, useCourseDetails, useOnlineCourseDetails, useNearbyCourses } from '../hooks/useCourseSearch'
import { useUserLocation } from '../hooks/useUserLocation'
import { sortByProximity, formatDistance } from '../utils/geolocation'

/**
 * Searchable course picker that searches Supabase, with an online fallback.
 * Replaces the hardcoded <select> dropdown in StepCourse.
 */
export default function CourseSearch({ selectedCourse, onSelectCourse, selectedTee, onSelectTee }) {
  const [query, setQuery] = useState(selectedCourse?.name || selectedCourse?.course_name || selectedCourse?.shortName || selectedCourse?.short_name || '')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [showResults, setShowResults] = useState(false)
  const [searchOnline, setSearchOnline] = useState(false)
  const [pendingOnlineId, setPendingOnlineId] = useState(null)
  const [manualSlope, setManualSlope] = useState('')
  const [manualRating, setManualRating] = useState('')
  const [showTeeDropdown, setShowTeeDropdown] = useState(false)
  const inputRef = useRef(null)
  const containerRef = useRef(null)
  const teeDropdownRef = useRef(null)

  // GPS location + nearby courses
  const userLocation = useUserLocation()
  const { data: nearbyCourses } = useNearbyCourses(userLocation?.latitude, userLocation?.longitude)

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300)
    return () => clearTimeout(timer)
  }, [query])

  // Supabase search (primary)
  const { data: dbResults, isLoading: dbLoading } = useCourseSearch(debouncedQuery)

  // Online search (fallback, user-triggered)
  const { data: onlineResults, isLoading: onlineLoading } = useOnlineCourseSearch(debouncedQuery, searchOnline)

  // Fetch full details for a selected online course
  const { data: onlineCourseDetail } = useOnlineCourseDetails(pendingOnlineId, !!pendingOnlineId)

  // When online course details arrive, select it
  useEffect(() => {
    if (onlineCourseDetail && pendingOnlineId) {
      onSelectCourse(onlineCourseDetail)
      setPendingOnlineId(null)
      setShowResults(false)
      setQuery(onlineCourseDetail.course_name || onlineCourseDetail.short_name || '')
    }
  }, [onlineCourseDetail, pendingOnlineId])

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowResults(false)
      }
      if (teeDropdownRef.current && !teeDropdownRef.current.contains(e.target)) {
        setShowTeeDropdown(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleSelectDbCourse = async (result) => {
    setShowResults(false)
    setQuery(result.course_name || result.short_name || '')
    setSearchOnline(false)

    // Fetch full details from Supabase
    const { getCourseFromDb } = await import('../api/courses')
    const full = await getCourseFromDb(result.id)
    if (full) {
      onSelectCourse(full)
    }
  }

  const handleSelectOnlineCourse = (result) => {
    setQuery(result.course_name || '')
    setPendingOnlineId(result.id)
    setShowResults(false)
  }

  const handleClear = () => {
    setQuery('')
    onSelectCourse(null)
    onSelectTee(null)
    setSearchOnline(false)
    setPendingOnlineId(null)
  }

  const handleTeeSelect = (tee) => {
    // If tee is missing slope/rating, check for manual values
    const finalTee = { ...tee }
    if (!finalTee.slope && manualSlope) finalTee.slope = parseFloat(manualSlope)
    if (!finalTee.rating && manualRating) finalTee.rating = parseFloat(manualRating)
    onSelectTee(finalTee)
  }

  const rawResults = dbResults || []
  const results = userLocation
    ? sortByProximity(rawResults, userLocation.latitude, userLocation.longitude)
    : rawResults
  const onlineResultsList = onlineResults || []
  const isLoading = dbLoading || onlineLoading || !!pendingOnlineId
  const hasResults = results.length > 0 || onlineResultsList.length > 0
  const showOnlineOption = debouncedQuery.length >= 3 && results.length < 5 && !searchOnline
  const showNearby = !debouncedQuery && nearbyCourses?.length > 0

  return (
    <div ref={containerRef}>
      {/* Search input */}
      <div style={{ position: 'relative' }}>
        <Search size={16} style={{
          position: 'absolute',
          left: '0.75rem',
          top: '50%',
          transform: 'translateY(-50%)',
          color: 'var(--text-muted)',
          pointerEvents: 'none',
        }} />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setShowResults(true)
            setSearchOnline(false)
            if (!e.target.value) handleClear()
          }}
          onFocus={() => {
            if (query.length >= 3 || nearbyCourses?.length > 0) setShowResults(true)
          }}
          placeholder="Search for a golf course..."
          style={{ paddingLeft: '2.25rem', paddingRight: selectedCourse ? '2rem' : undefined }}
        />
        {selectedCourse && (
          <button
            onClick={handleClear}
            style={{
              position: 'absolute',
              right: '0.75rem',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-muted)',
              fontSize: '1.25rem',
              lineHeight: 1,
              padding: '0.25rem',
              borderRadius: '9999px',
            }}
          >
            &times;
          </button>
        )}
        {isLoading && (
          <span style={{
            position: 'absolute',
            right: selectedCourse ? '2.25rem' : '0.75rem',
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
          }}>
            <Loader2 size={16} style={{
              color: 'var(--text-muted)',
              animation: 'spin 1s linear infinite',
            }} />
          </span>
        )}
      </div>

      {/* Results dropdown */}
      {showResults && (debouncedQuery.length >= 3 || showNearby) && (
        <div style={{
          marginTop: '0.25rem',
          backgroundColor: 'var(--bg-secondary)',
          border: '1px solid var(--border-color)',
          borderRadius: '0.5rem',
          maxHeight: '280px',
          overflowY: 'auto',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        }}>
          {/* Nearby courses (shown before typing) */}
          {showNearby && (
            <>
              <div style={{
                padding: '0.5rem 0.75rem 0.25rem',
                fontSize: '0.6875rem',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'var(--text-muted)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
              }}>
                <Navigation size={10} />
                Nearby
              </div>
              {nearbyCourses.map((r) => (
                <button
                  key={`nearby-${r.id}`}
                  onClick={() => handleSelectDbCourse(r)}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.125rem',
                    width: '100%',
                    padding: '0.625rem 0.75rem',
                    textAlign: 'left',
                    borderBottom: '1px solid var(--border-color)',
                    borderRadius: '0',
                  }}
                >
                  <span style={{ fontWeight: '600', fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                    {r.short_name || r.course_name}
                  </span>
                  {r.club_name && r.club_name !== r.course_name && (
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{r.club_name}</span>
                  )}
                  <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <MapPin size={10} />
                    {[r.city, r.state].filter(Boolean).join(', ')}
                    {r.distance_miles != null && (
                      <span style={{ color: 'var(--accent-primary)' }}>
                        {' '}· {formatDistance(r.distance_miles)}
                      </span>
                    )}
                  </span>
                </button>
              ))}
            </>
          )}

          {/* DB results */}
          {debouncedQuery.length >= 3 && results.map((r) => (
            <button
              key={r.id}
              onClick={() => handleSelectDbCourse(r)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.125rem',
                width: '100%',
                padding: '0.625rem 0.75rem',
                textAlign: 'left',
                borderBottom: '1px solid var(--border-color)',
                borderRadius: '0',
              }}
            >
              <span style={{ fontWeight: '600', fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                {r.short_name || r.course_name}
              </span>
              {r.club_name && r.club_name !== r.course_name && (
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{r.club_name}</span>
              )}
              {(r.city || r.state) && (
                <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <MapPin size={10} />
                  {[r.city, r.state].filter(Boolean).join(', ')}
                  {r._distanceMiles != null && (
                    <span style={{ color: 'var(--accent-primary)' }}>
                      {' '}· {formatDistance(r._distanceMiles)}
                    </span>
                  )}
                </span>
              )}
            </button>
          ))}

          {/* Online results */}
          {searchOnline && onlineResultsList.map((r) => (
            <button
              key={`online-${r.id}`}
              onClick={() => handleSelectOnlineCourse(r)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.125rem',
                width: '100%',
                padding: '0.625rem 0.75rem',
                textAlign: 'left',
                borderBottom: '1px solid var(--border-color)',
                backgroundColor: 'rgba(var(--accent-secondary-rgb, 245, 158, 11), 0.03)',
                borderRadius: '0',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <Globe size={12} style={{ color: 'var(--accent-secondary, #f59e0b)', flexShrink: 0 }} />
                <span style={{ fontWeight: '600', fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                  {r.course_name}
                </span>
              </div>
              {(r.city || r.state) && (
                <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', paddingLeft: '1.25rem' }}>
                  {[r.city, r.state].filter(Boolean).join(', ')}
                </span>
              )}
            </button>
          ))}

          {/* No results message */}
          {!isLoading && !hasResults && !showNearby && debouncedQuery.length >= 3 && (
            <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
              No courses found
            </div>
          )}

          {/* "Search online" option */}
          {showOnlineOption && (
            <button
              onClick={() => setSearchOnline(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.375rem',
                width: '100%',
                padding: '0.625rem',
                color: 'var(--accent-secondary, #f59e0b)',
                fontSize: '0.8125rem',
                fontWeight: '600',
                borderTop: results.length > 0 ? '1px solid var(--border-color)' : 'none',
                borderRadius: '0',
              }}
            >
              <Globe size={14} />
              Lookup Course
            </button>
          )}
        </div>
      )}

      {/* Selected course info */}
      {selectedCourse && (
        <div style={{
          marginTop: '0.5rem',
          padding: '0.625rem 0.75rem',
          backgroundColor: 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.06)',
          borderRadius: '0.5rem',
          border: '1px solid var(--accent-primary)',
        }}>
          <div style={{ fontWeight: '600', fontSize: '0.875rem', color: 'var(--text-primary)' }}>
            {selectedCourse.short_name || selectedCourse.course_name}
          </div>
          {selectedCourse.club_name && selectedCourse.club_name !== selectedCourse.course_name && (
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              {selectedCourse.club_name}
            </div>
          )}
          {(selectedCourse.city || selectedCourse.state) && (
            <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.25rem', marginTop: '0.125rem' }}>
              <MapPin size={10} />
              {[selectedCourse.city, selectedCourse.state].filter(Boolean).join(', ')}
            </div>
          )}
        </div>
      )}

      {/* Tee picker dropdown */}
      {selectedCourse && selectedCourse.tees?.length > 0 && (
        <div style={{ marginTop: '0.75rem' }} ref={teeDropdownRef}>
          <label style={{ display: 'block', marginBottom: '0.375rem' }}>Tees</label>
          <button
            type="button"
            onClick={() => setShowTeeDropdown(!showTeeDropdown)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              padding: '0.625rem 0.75rem',
              borderRadius: '0.5rem',
              backgroundColor: selectedTee ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.06)' : 'var(--bg-tertiary)',
              border: `1px solid ${selectedTee ? 'var(--accent-primary)' : 'var(--border-color)'}`,
              textAlign: 'left',
              cursor: 'pointer',
            }}
          >
            {selectedTee ? (
              <>
                <div>
                  <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>{selectedTee.name}</span>
                  {selectedTee.totalYardage > 0 && (
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', marginLeft: '0.5rem' }}>
                      {selectedTee.totalYardage.toLocaleString()} yds
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {selectedTee.rating && selectedTee.slope && (
                    <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                      {selectedTee.rating} / {selectedTee.slope}
                    </span>
                  )}
                  <ChevronDown size={16} style={{
                    color: 'var(--text-muted)',
                    transition: 'transform 0.2s',
                    transform: showTeeDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                  }} />
                </div>
              </>
            ) : (
              <>
                <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Select tees</span>
                <ChevronDown size={16} style={{
                  color: 'var(--text-muted)',
                  transition: 'transform 0.2s',
                  transform: showTeeDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                }} />
              </>
            )}
          </button>
          {showTeeDropdown && (
            <div style={{
              marginTop: '0.25rem',
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: '0.5rem',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            }}>
              {selectedCourse.tees.map((tee) => {
                const isSelected = selectedTee?.name === tee.name
                return (
                  <button
                    key={tee.name}
                    onClick={() => {
                      handleTeeSelect(tee)
                      setShowTeeDropdown(false)
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      padding: '0.625rem 0.75rem',
                      textAlign: 'left',
                      borderBottom: '1px solid var(--border-color)',
                      backgroundColor: isSelected ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.08)' : 'transparent',
                      borderRadius: '0',
                    }}
                  >
                    <div>
                      <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>{tee.name}</span>
                      {tee.totalYardage > 0 && (
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', marginLeft: '0.5rem' }}>
                          {tee.totalYardage.toLocaleString()} yds
                        </span>
                      )}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      {tee.rating && tee.slope ? (
                        <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                          {tee.rating} / {tee.slope}
                        </span>
                      ) : (
                        <span style={{ fontSize: '0.75rem', color: 'var(--accent-secondary, #f59e0b)' }}>
                          Enter rating/slope
                        </span>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Manual slope/rating entry for tees missing data */}
      {selectedTee && (!selectedTee.slope || !selectedTee.rating) && (
        <div style={{
          marginTop: '0.5rem',
          padding: '0.75rem',
          backgroundColor: 'rgba(245, 158, 11, 0.06)',
          borderRadius: '0.5rem',
          border: '1px solid rgba(245, 158, 11, 0.25)',
        }}>
          <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--accent-secondary, #f59e0b)', marginBottom: '0.5rem' }}>
            Slope & Rating needed for handicap calculations
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            {!selectedTee.rating && (
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>
                  Course Rating
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={manualRating}
                  onChange={(e) => {
                    setManualRating(e.target.value)
                    if (e.target.value) {
                      onSelectTee({ ...selectedTee, rating: parseFloat(e.target.value) })
                    }
                  }}
                  placeholder="e.g. 72.5"
                  style={{ width: '100%', height: '36px', textAlign: 'center' }}
                />
              </div>
            )}
            {!selectedTee.slope && (
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', display: 'block', marginBottom: '0.25rem' }}>
                  Slope
                </label>
                <input
                  type="number"
                  value={manualSlope}
                  onChange={(e) => {
                    setManualSlope(e.target.value)
                    if (e.target.value) {
                      onSelectTee({ ...selectedTee, slope: parseInt(e.target.value) })
                    }
                  }}
                  placeholder="e.g. 127"
                  style={{ width: '100%', height: '36px', textAlign: 'center' }}
                />
              </div>
            )}
          </div>
          <div style={{ fontSize: '0.625rem', color: 'var(--text-muted)', marginTop: '0.375rem' }}>
            Check the physical scorecard or GHIN app for these values
          </div>
        </div>
      )}
    </div>
  )
}
